import Api from './api';
import * as actionTypes from './actionTypes';
import * as eventTypes from './eventTypes';
import { sendHttpCommandAndWaitForEvent, sendHttpRequest } from 'actions/actionCreators/httpActions';
import { showError } from 'actions/actionCreators/snackbarActions';
import { camelizeKeys } from 'humps';

export function loadSignees(documentId, siteId) {
  return async (dispatch, getState) => {
    const key = `${documentId}-${siteId}`;
    let loading = false,
      loaded = false;

    let documentSignees = getState().document.documents.documentSignees[key];
    if (documentSignees) {
      loading = documentSignees.loading;
      loaded = documentSignees.loaded;
    }

    if (loading || loaded) return;

    try {
      dispatch({ type: actionTypes.LOAD_DOCUMENT_SIGNEES_STARTED, id: key });
      documentSignees = await dispatch(sendHttpRequest(Api.getDocumentSignees(documentId, siteId)));
      dispatch({ type: actionTypes.LOAD_DOCUMENT_SIGNEES_SUCCEEDED, payload: documentSignees, id: key });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadDocumentEntities(documentId, siteId) {
  return async (dispatch, getState) => {
    const key = `${documentId}-${siteId}`;
    let loading = false,
      loaded = false;

    let documentEntities = getState().document.documents.documentEntities[key];
    if (documentEntities) {
      loading = documentEntities.loading;
      loaded = documentEntities.loaded;
    }

    if (loading || loaded) return;

    try {
      dispatch({ type: actionTypes.LOAD_DOC_ENTITIES_STARTED, id: key });
      documentEntities = await dispatch(sendHttpRequest(Api.getDocumentEntities(documentId, siteId)));
      dispatch({ type: actionTypes.LOAD_DOC_ENTITIES_SUCCEEDED, payload: documentEntities, id: key });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadGuarantors(documentId, siteId) {
  return async (dispatch, getState) => {
    const key = `${documentId}-${siteId}`;
    let loading = false,
    loaded = false;
    let guarantors = getState().document.guarantors[key];

    if (guarantors) {
      loading = guarantors.loading;
      loaded = guarantors.loaded;
    }

    if (loading || loaded) return;

    try {
      dispatch({ type: actionTypes.LOAD_GUARANTORS_STARTED, key });
      guarantors = await dispatch(sendHttpRequest(Api.getGuarantors(documentId, siteId)));
      const result = camelizeKeys(guarantors);
      dispatch({
        type: actionTypes.LOAD_GUARANTORS_SUCCEEDED,
        key,
        guarantors: result
      });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadDocumentPersons(documentId, siteId, partnerId) {
  return async (dispatch, getState) => {
    let loading = false,
      loaded = false;

    let documentPersons = getState().document.documents.documentPersons[partnerId];
    if (documentPersons) {
      loading = documentPersons.loading;
      loaded = documentPersons.loaded;
    }

    if (loading || loaded) return;

    try {
      dispatch({ type: actionTypes.LOAD_DOCUMENT_PERSONS_STARTED, id: partnerId });
      documentPersons = await dispatch(sendHttpRequest(Api.getPersons(documentId, siteId, partnerId)));
      dispatch({ type: actionTypes.LOAD_DOCUMENT_PERSONS_SUCCEEDED, payload: documentPersons, id: partnerId });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function updateDocumentSignees(documentId, siteId, documentSignees) {
  return async dispatch => {
    const key = `${documentId}-${siteId}`;
    try {
      dispatch({ type: actionTypes.SAVE_DOCUMENT_SIGNEES_STARTED, id: key });
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(Api.updateDocumentSignees(documentId, siteId, documentSignees), 'UpdateDocumentSignees', [
          eventTypes.DOCUMENTS_SIGNEES_UPDATED
        ])
      );
      if (event.ok) {
        dispatch({ type: actionTypes.DOCUMENT_SIGNEES_UPDATED, payload: event.eventBody.documentSignees, id: key });
      }
      dispatch({ type: actionTypes.SAVE_DOCUMENT_SIGNEES_FINISHED, id: key });
      return event;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function buildDownloadUrl(documentId, siteId, filePath, fileName) {
  return () => Api.buildDownloadUrl(documentId, siteId, filePath, fileName);
}

export function buildDMSDownloadUrl(fileId) {
  return () => Api.buildDMSDownloadUrl(fileId);
}

export function getBulkFilesUrl(fileName, fileIds, exportAllDocumentVersions) {
  return Api.getBulkFilesUrl(fileName, fileIds, exportAllDocumentVersions);
}

export function buildExportDownloadUrl(filePath, fileName) {
  return () => Api.buildExportDownloadUrl(filePath, fileName);
}

export function dismissDocumentWorkflowError(documentWorkflowErrorId) {
  return async dispatch => {
    try {
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(Api.dismissDocumentWorkflowError(documentWorkflowErrorId), 'DismissDocumentWorkflowError', [
          eventTypes.DOCUMENT_WORKFLOW_ERROR_DISMISSED
        ])
      );
      return event;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function validateStateChange(documentId, siteId, oldStateId, newStateId, callback = () => {}) {
  return async dispatch => {
    try {
      const validationResponse = await dispatch(sendHttpRequest(Api.validateStateChange(documentId, siteId, oldStateId, newStateId)));
      if (!validationResponse.isValidationSuccessful) {
        dispatch(showError(validationResponse.errorMessage));
      } else {
        callback();
      }
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadDocumentSecurity(documentId, siteId) {
  return () => Api.getDocumentSecurity(documentId, siteId);
}
